import {defaultPrintProps} from '../sidebars/PrintSidebar';
import {
    MapboxControlButton,
    MapboxControlGroup,
    createReactMapboxControl
} from '../../../utils/mapbox/Mapbox';
import {stringifyUrlProps} from '../urlProps';
import FolderIcon from '@material-ui/icons/Folder';
import fetchUser from '../../../utils/gaia/fetchUser';
import useSyncPromise from '../../../utils/react/useSyncPromise';
import LayersIcon from '@material-ui/icons/Layers';
import PrintIcon from '@material-ui/icons/Print';
import React, {useCallback} from 'react';
import SubscriptionTooltip from '../SubscriptionTooltip';
import {capitalize} from 'lodash';
import trackAnalytics from '../../../utils/gaia/trackAnalytics';
import CakeIcon from '@material-ui/icons/Cake';
import {SPRING_1} from '../../../colors';

export interface SidebarControlProps {
    interaction: MapInteraction;
    showPrintControl: boolean;
    bounds: mapboxgl.LngLatBounds | undefined;
    zoom: number | undefined;
    highlight: MapHighlight;
    userLayers: UserLayer[] | undefined;
    onSidebarChange: (sidebar: MapSidebar) => void;
    sidebar: MapSidebar;
    showLayersControl: boolean;
}

export default createReactMapboxControl(({
    interaction,
    showPrintControl,
    bounds,
    zoom,
    highlight,
    userLayers,
    onSidebarChange,
    sidebar,
    showLayersControl
}: SidebarControlProps) => {
    const [user] = useSyncPromise(fetchUser());

    const onSidebarChangeAnalytics = useCallback((nextSidebar: MapSidebar) => {
        if (nextSidebar.type === 'hidden') {
            trackAnalytics(`Close ${capitalize(sidebar.type)} Sidebar`, {category: 'map2'});
        } else {
            trackAnalytics(`Open ${capitalize(nextSidebar.type)} Sidebar`, {category: 'map2'});
        }
        onSidebarChange(nextSidebar);
    }, [onSidebarChange, sidebar.type]);

    const toggleSidebar = useCallback((nextSidebar: MapSidebar) => {
        if (sidebar.type !== nextSidebar.type) {
            onSidebarChangeAnalytics(nextSidebar);
        } else {
            onSidebarChangeAnalytics({type: 'hidden'});
        }
    }, [onSidebarChangeAnalytics, sidebar.type]);

    const isLayerSidebar = (
        sidebar.type === 'layers' ||
        sidebar.type === 'layerDetails' ||
        sidebar.type === 'editLayers'
    );

    return <MapboxControlGroup>
        {showLayersControl && <MapboxControlButton
            onClick={useCallback(() => {
                if (isLayerSidebar) {
                    onSidebarChangeAnalytics({type: 'hidden'});
                } else {
                    onSidebarChangeAnalytics({type: 'layers'});
                }
            }, [isLayerSidebar, onSidebarChangeAnalytics])}
            style={isLayerSidebar ? {color: SPRING_1} : undefined}
        >
            <SubscriptionTooltip
                placement='right'
                title='Layers & Overlays'
                subscription={undefined}
                enterDelay={750}
            >
                <LayersIcon style={{width: 24, height: 24}} />
            </SubscriptionTooltip>
        </MapboxControlButton>}

        {process.env.NODE_ENV !== 'production' && showLayersControl && <MapboxControlButton
            onClick={useCallback(() => toggleSidebar({type: 'data'}), [toggleSidebar])}
            disabled={!user || !user.isAuthenticated}
            style={sidebar.type === 'data' ? {color: SPRING_1} : undefined}
        >
            <SubscriptionTooltip
                placement='right'
                title='Manage waypoints, routes, tracks, and folders'
                subscription='user'
                enterDelay={750}
            >
                <FolderIcon style={{width: 24, height: 24}} />
            </SubscriptionTooltip>
        </MapboxControlButton>}

        {showPrintControl && <MapboxControlButton
            style={sidebar.type === 'print' ? {color: SPRING_1} : undefined}
            onClick={useCallback(() => {
                if (window.location.pathname !== '/map/') {
                    if (!userLayers) return;
                    window.location.assign('/map/' + stringifyUrlProps({
                        center: bounds && bounds.getCenter(),
                        zoom,
                        highlight,
                        layers: userLayers
                            .filter(userLayer => userLayer.isVisible)
                            .map(userLayer => userLayer.layer.type),
                        sidebar: defaultPrintProps
                    }));
                } else {
                    toggleSidebar(defaultPrintProps);
                }
            }, [bounds, highlight, toggleSidebar, userLayers, zoom])}
            disabled={(
                (user && !user.hasSubscription) ||
                interaction.type === 'movingWaypoint' ||
                interaction.type === 'editingRoute'
            )}
        >
            <SubscriptionTooltip
                enterDelay={750}
                placement='right'
                title='Print'
                subscription='member'
            >
                <PrintIcon style={{width: 24, height: 24}} />
            </SubscriptionTooltip>
        </MapboxControlButton>}

        {process.env.NODE_ENV !== 'production' && <MapboxControlButton
            onClick={useCallback(() => toggleSidebar({type: 'performance'}), [toggleSidebar])}
            style={sidebar.type === 'performance' ? {color: SPRING_1} : undefined}
        >
            <CakeIcon style={{width: 24, height: 24, marginTop: -2}} />
        </MapboxControlButton>}

    </MapboxControlGroup>;
});
